<template>
  <div class="eg-input" :class="[variantClass, sizeClass, ...stateClasses]">
    <label v-if="showLabel" class="eg-input__label">{{ label }}</label>

    <div class="eg-input__control">
      <div v-if="isSearch" class="eg-input__prefix">
        <eg-icon
          icon="search"
          class="search-icon"
          size="medium"
          color="var(--color-surface-neutral-container-5)"
        />
      </div>

      <component
        :is="innerTag"
        ref="inner"
        class="eg-input__inner"
        :value="variant === 'textarea' ? undefined : value"
        :placeholder="placeholder"
        :number="number"
        :min="min"
        :max="max"
        :step="step"
        :type="type"
        :rows="rows"
        :maxlength="maxLength"
        :disabled="disabledComputed"
        @input="onInput"
        @change="onChange"
      >
        <template v-if="variant === 'textarea'">{{ value }}</template>
      </component>

      <button
        v-if="showClear"
        type="button"
        class="eg-input__clear d-flex"
        aria-label="Clear"
        @click="clearValue"
      >
        <eg-icon icon="close-circle" size="small" />
      </button>

      <div
        v-if="showCounter && variant === 'textarea'"
        class="eg-input__char-counter"
      >
        {{ (value || "").length }}
        <template v-if="maxLength">/{{ maxLength }}</template>
      </div>
    </div>

    <div v-if="showCaption" class="eg-input__caption">{{ caption }}</div>

    <div v-if="showValidation && message" class="eg-input__footer">
      <div class="eg-input__message">{{ message }}</div>
    </div>
  </div>
</template>

<script>
import EgIcon from "@/components/global/eg/eg_icon.vue";

export default {
  name: "EgInput",
  components: { EgIcon },
  props: {
    value: { type: [String, Number], default: "" },
    min: { type: [String, Number], default: "" },
    max: { type: [String, Number], default: "" },
    step: { type: [String, Number], default: "" },
    number: { type: Boolean, default: false },
    type: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    label: { type: String, default: "" },
    caption: { type: String, default: "" },
    message: { type: String, default: "" },
    variant: {
      type: String,
      default: "input", // input | textarea | search | inline-search
      validator: (v) =>
        ["input", "textarea", "search", "inline-search"].includes(v),
    },
    // medium: Subject, small: Class, extra-small: Assignment
    size: {
      type: String,
      default: "small",
      validator: (v) => ["medium", "small", "extra-small"].includes(v),
    },
    state: {
      type: String,
      default: "default", // default | hover | active | active-with-hint | filled | disabled | error | error-active | warning | warning-active | success | success-active
      validator: (v) =>
        [
          "default",
          "hover",
          "active",
          "active-with-hint",
          "filled",
          "disabled",
          "error",
          "error-active",
          "warning",
          "warning-active",
          "success",
          "success-active",
        ].includes(v),
    },
    disabled: { type: Boolean, default: false },
    // textarea
    rows: { type: Number, default: 3 },
    autoResize: { type: Boolean, default: false },
    maxLength: { type: Number, default: null },
    showCounter: { type: Boolean, default: false },
    // search
    clearable: { type: Boolean, default: false },
    // validation/footer visibility
    showValidation: { type: Boolean, default: true },
  },
  data() {
    return {
      hasContent: !!this.value,
    };
  },
  computed: {
    showLabel() {
      return Boolean(this.label);
    },
    showCaption() {
      return Boolean(this.caption);
    },
    sizeClass() {
      return `eg-input_size-${this.size}`;
    },
    stateClasses() {
      const effective = this.disabledComputed ? "disabled" : this.state;
      const classes = [`is-${effective}`];
      if (effective.endsWith("-active")) classes.push("is-active");
      if (effective === "active-with-hint") classes.push("is-active");
      if (this.isFilled) classes.push("is-filled");
      return classes;
    },
    variantClass() {
      return `eg-input_variant-${this.variant}`;
    },
    innerTag() {
      return this.variant === "textarea" ? "textarea" : "input";
    },
    isSearch() {
      return this.variant === "search" || this.variant === "inline-search";
    },
    disabledComputed() {
      // For search variants disabled is not used
      return this.variant === "search" || this.variant === "inline-search"
        ? false
        : this.disabled;
    },
    isFilled() {
      return Boolean(this.hasContent);
    },
    showClear() {
      return (
        (this.isSearch || this.clearable) &&
        this.isFilled &&
        !this.disabledComputed
      );
    },
  },
  watch: {
    value(val) {
      this.hasContent = !!val;
      if (this.variant === "textarea" && this.autoResize) {
        this.$nextTick(this.resizeTextarea);
      }
    },
  },
  mounted() {
    if (this.variant === "textarea" && this.autoResize) {
      this.resizeTextarea();
    }
  },
  methods: {
    onInput(event) {
      let next = event && event.target ? event.target.value : event;

      // Prevent negative values for number inputs with min >= 0
      if (this.type === "number" && this.min !== "" && this.min >= 0) {
        const numValue = parseFloat(next);
        if (!isNaN(numValue) && numValue < 0) {
          next = 0;
          // Update the actual input element value
          if (this.$refs.inner) {
            this.$refs.inner.value = 0;
          }
        }
      }

      this.hasContent = Boolean(next && next.length > 0);
      this.$emit("input", next);
      this.$emit("onChange", next);
      if (this.variant === "textarea" && this.autoResize) {
        this.resizeTextarea();
      }
    },
    onChange(event) {
      const next = event && event.target ? event.target.value : event;
      this.$emit("change", next);
    },
    clearValue() {
      this.hasContent = false;
      this.$emit("input", "");
      this.$emit("onChange", "");
      if (this.$refs.inner) this.$refs.inner.value = "";
      this.$nextTick(() => {
        if (this.$refs.inner && this.$refs.inner.focus)
          this.$refs.inner.focus();
      });
    },
    resizeTextarea() {
      const el = this.$refs.inner;
      if (!el || el.tagName !== "TEXTAREA") return;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    },
  },
};
</script>

<style scoped lang="scss">
.eg-input {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.eg-input__label {
  color: var(--color-text-neutral-on-white);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0;
}

.eg-input__caption {
  color: var(--color-text-neutral-fade);
  font-size: 12px;
}

.eg-input__control {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid var(--color-stroke-neutral-default);
  border-radius: 8px;
  background: #fff;
  transition: background-color 120ms ease, box-shadow 120ms ease,
    border-color 120ms ease;
}

/* Native interactions mapping */
.eg-input__control:hover {
  border-color: var(--color-stroke-neutral-hover);
}

.eg-input:focus-within .eg-input__control {
  border-color: var(--color-stroke-primary-default);
  box-shadow: 0 0 0 2px var(--color-surface-primary-opacity-focus);
}

.eg-input__inner {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: var(--color-text-neutral-on-white);
  padding: 6px 8px;
  font-size: 14px;
  line-height: 22px;
}

.eg-input_size-medium:not(.eg-input_variant-textarea) {
  input,
  .eg-input__control {
    height: 36px;
  }
}

.eg-input_size-medium .eg-input__inner {
  font-size: 14px;
  font-weight: 400;
}

.eg-input_size-small:not(.eg-input_variant-textarea) .eg-input__control {
  height: 32px;
}

.eg-input_size-small .eg-input__inner {
  font-size: 14px;
  font-weight: 400;
}

.eg-input_size-extra-small .eg-input__inner {
  font-size: 12px;
  font-weight: 400;
}

.eg-input__inner::placeholder {
  color: var(--color-text-neutral-disabled);
}

/* Search icons */
.eg-input__prefix {
  padding-left: 8px;
  display: flex;
  align-items: center;
  color: var(--color-icon-neutral-default);
}

.eg-input_variant-inline-search .eg-input__inner {
  padding-left: 32px;
}

.eg-input__clear {
  position: absolute;
  right: 6px;
  top: 50%;
  padding-left: 8px;
  transform: translateY(-50%);
  border: none;
  border-left: 1px solid var(--color-surface-neutral-container-4);
  background-color: white;
  cursor: pointer;
  color: var(--color-text-secondary);
}

/* Inline search compact spacing */
.eg-input_variant-inline-search .eg-input__inner {
  padding-top: 6px;
  padding-bottom: 6px;
}

/* Textarea char counter pinned bottom-right inside control */
.eg-input__char-counter {
  position: absolute;
  right: 8px;
  bottom: 8px;
  font-size: 12px;
  color: var(--color-text-neutral-fade);
  pointer-events: none;
}

/* States */
.eg-input.is-hover .eg-input__control {
  border-color: var(--color-stroke-neutral-hover);
}

.eg-input.is-active .eg-input__control {
  border-color: var(--color-stroke-primary-default);
  box-shadow: 0 0 0 2px var(--color-surface-primary-opacity-focus);
}

.eg-input.is-filled .eg-input__control {
  background-color: var(--color-surface-neutral-default);
  border-color: var(--color-stroke-neutral-default);
}

/* Ensure hover wins over filled */
.eg-input.is-filled .eg-input__control:hover {
  border-color: var(--color-stroke-neutral-hover);
}

.eg-input.is-disabled .eg-input__control {
  background: var(--color-surface-neutral-disabled);
  border-color: var(--color-stroke-neutral-disabled);
}

.eg-input.is-disabled .eg-input__inner {
  color: var(--color-text-neutral-disabled);
}

.eg-input_variant-search.is-disabled .eg-input__prefix {
  color: var(--color-icon-neutral-disabled);
}

.eg-input.is-error .eg-input__control {
  border-color: var(--color-stroke-semantic-onError);
  background: var(--color-surface-semantic-error-light);
}

.eg-input.is-error-active .eg-input__control {
  border-color: var(--color-stroke-semantic-onError);
  background: var(--color-components-input-inputBackground, transparent);
  box-shadow: 0 0 0 2px var(--color-surface-semantic-error-opacity-focus);
}

.eg-input.is-error:focus-within .eg-input__control {
  border-color: var(--color-stroke-semantic-onError);
  background: var(--color-components-input-inputBackground, transparent);
  box-shadow: 0 0 0 2px var(--color-surface-semantic-error-opacity-focus);
}

.eg-input.is-warning .eg-input__control {
  border-color: var(--color-stroke-semantic-onWarning);
  background: var(--color-surface-semantic-warning-light);
}

.eg-input.is-warning-active .eg-input__control {
  border-color: var(--color-stroke-semantic-onWarning);
  background: var(--color-components-input-inputBackground, transparent);
  box-shadow: 0 0 0 2px var(--color-surface-semantic-error-opacity-focus);
}

.eg-input.is-warning:focus-within .eg-input__control {
  border-color: var(--color-stroke-semantic-onWarning);
  background: var(--color-components-input-inputBackground, transparent);
  box-shadow: 0 0 0 2px var(--color-surface-semantic-error-opacity-focus);
}

.eg-input.is-success .eg-input__control {
  border-color: var(--color-stroke-semantic-onSuccess);
  background: var(--color-surface-semantic-success-light);
}

.eg-input.is-success-active .eg-input__control {
  border-color: var(--color-stroke-semantic-onSuccess);
  background: var(--color-components-input-inputBackground, transparent);
  box-shadow: 0 0 0 2px var(--color-surface-semantic-success-opacity-focus);
}

.eg-input.is-success:focus-within .eg-input__control {
  border-color: var(--color-stroke-semantic-onSuccess);
  background: var(--color-components-input-inputBackground, transparent);
  box-shadow: 0 0 0 2px var(--color-surface-semantic-success-opacity-focus);
}

.eg-input__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 16px; /* reserve space for validation/counter */
}

.eg-input__message {
  color: var(--color-text-neutral-fade);
  font-size: 12px;
}

.eg-input.is-error .eg-input__message,
.eg-input.is-error-active .eg-input__message {
  color: var(--color-text-semantic-onError);
}

.eg-input.is-warning .eg-input__message,
.eg-input.is-warning-active .eg-input__message {
  color: var(--color-text-semantic-onWarning);
}

.eg-input.is-success .eg-input__message,
.eg-input.is-success-active .eg-input__message {
  color: var(--color-text-semantic-onSuccess);
}

.eg-input__counter {
  color: var(--color-text-neutral-fade);
  font-size: 12px;
}

/* Priority rules: focus should always dominate over 'filled' or 'hover' */
.eg-input.is-filled:focus-within .eg-input__control {
  border-color: var(--color-stroke-primary-default);
  box-shadow: 0 0 0 2px var(--color-surface-primary-opacity-focus);
}
</style>
